import React from "react";
import { Box } from "@mui/system";
import { Button, CircularProgress } from "@mui/joy";
import DarkInput from "../common/DarkInput";
import DarkTypography from "../common/DarkTypography";
import FooterBrand from "../common/FooterBrand";
import IconButtonCTA from "../common/IconButtonCTA";
import {
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../styles/StyledMUI";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import type { LoginForm } from "../../api/auth/Auth.typed";
import { authCardDarkInputSx, colorTokens, withAlpha } from "../../styles/globalStyles";
import { useZekayAuthMark } from "../../utils/useZekayAuthMark";

export default function ResetPasswordPage() {
  const { src: zekayIconSrc, onImgError: onZekayIconError } = useZekayAuthMark();
  const [password1, setPassword1] = React.useState<string>("");
  const [password2, setPassword2] = React.useState<string>("");
  const [showPassword1, setShowPassword1] = React.useState<boolean>(false);
  const [showPassword2, setShowPassword2] = React.useState<boolean>(false);
  const [isValid, setIsValid] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { verifyToken, resetPassword } = useAuth();
  const { showAlert } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();

  const handleReset = () => {
    const token = new URLSearchParams(location.search).get("token");
    const email = new URLSearchParams(location.search).get("email");

    if (password1.length >= 8) {
      if (token && email) {
        if (password1 === password2) {
          const h: LoginForm = {
            Email: email,
            Token: token,
            Password: password2,
          };

          setIsLoading(true);
          resetPassword(h)
            .then(() => {
              showAlert("Mot de passe réinitialisé avec succès !", "success");
              navigate("/");
            })
            .catch(() => showAlert("Une erreur est survenue. Veuillez réessayer.", "danger"))
            .finally(() => setIsLoading(false));
        } else {
          showAlert("Les mots de passe ne sont pas identiques", "danger");
        }
      } else {
        showAlert("Le lien est invalide ou a expiré.", "danger");
      }
    } else {
      showAlert("Le mot de passe doit contenir au moins 8 caractères.", "danger");
    }
  };

  React.useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");

    if (token) {
      const h: LoginForm = {
        Token: token,
      };

      verifyToken(h).then((r) => setIsValid(!!r));
    } else {
      setIsValid(false);
    }
  }, [location.search, verifyToken]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        overflowX: "hidden",
        padding: 2,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            textAlign: "center",
            backgroundColor: "background.body",
            padding: 3,
            borderRadius: 2,
            boxShadow: `0 4px 12px ${withAlpha(colorTokens.bg.overlay, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
            <img src={zekayIconSrc} width="84px" alt="Zekay icon" onError={onZekayIconError} />
          </Box>

          {isValid ? (
            <>
              <DarkTypography level="h4" sx={{ marginBottom: 2 }}>
                Réinitialisez votre mot de passe
              </DarkTypography>
              <DarkInput
                placeholder="Nouveau mot de passe"
                type={showPassword1 ? "text" : "password"}
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReset()}
                endDecorator={
                  <IconButtonCTA
                    ctaVariant="neutral"
                    aria-label="Afficher/masquer mot de passe"
                    onClick={() => setShowPassword1((v) => !v)}
                  >
                    {showPassword1 ? <VisibilityOff /> : <Visibility />}
                  </IconButtonCTA>
                }
                sx={authCardDarkInputSx}
              />
              <DarkInput
                placeholder="Confirmez le mot de passe"
                type={showPassword2 ? "text" : "password"}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReset()}
                endDecorator={
                  <IconButtonCTA
                    ctaVariant="neutral"
                    aria-label="Afficher/masquer confirmation du mot de passe"
                    onClick={() => setShowPassword2((v) => !v)}
                  >
                    {showPassword2 ? <VisibilityOff /> : <Visibility />}
                  </IconButtonCTA>
                }
                sx={authCardDarkInputSx}
              />
              <Button
                variant="solid"
                color="primary"
                sx={{ marginTop: 2, width: "100%" }}
                onClick={handleReset}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size="sm" /> : "Réinitialiser"}
              </Button>
            </>
          ) : (
            <>
              <DarkTypography level="h4" sx={{ marginBottom: 1.5 }}>
                Lien non valide
              </DarkTypography>
              <DarkTypography level="body-md" colorVariant="muted" sx={{ marginBottom: 2 }}>
                Cette page a expiré ou le lien n’est plus utilisable. Demandez un nouvel e-mail de
                réinitialisation depuis l’écran de connexion.
              </DarkTypography>
              <Button
                variant="plain"
                size="sm"
                onClick={() => navigate("/")}
                sx={{ fontSize: "0.8rem", marginY: 1 }}
              >
                Retour à la connexion
              </Button>
            </>
          )}
        </Box>
      </Box>
      <FooterBrand showChromeNote />
    </Box>
  );
}
